/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The DR API (/{project}/dr/..., docs/dr/DR_API.md). Every call is quiet: the page and its
 * dialogs show the API's own message (a setting Swift cannot honour, a record in use...).
 */

import Api from "@src/utils/ApiCaller";
import configLoader from "@src/utils/Config";

import type {
  DrEvent,
  DrApproval,
  DrGroup,
  DrPairCapabilities,
  DrPlan,
  DrPlanSpec,
  DrPlanVersion,
  DrPolicy,
  DrRecordKind,
  DrRecoveryPoint,
  DrRun,
  DrRunAction,
  DrClientSettings,
  DrClientAccess,
  DrAlert,
  DrOverview,
  DrSite,
  DrSla,
  DrWorkload,
  DrWorkloadAction,
} from "@src/@types/Dr";

/** kind -> [path segment, body key] */
const PATHS: { [kind in DrRecordKind]: [string, string] } = {
  site: ["sites", "site"],
  policy: ["policies", "policy"],
  protection_group: ["protection_groups", "protection_group"],
  workload: ["workloads", "workload"],
};

const base = () => `${configLoader.config.servicesUrls.coriolis}/${Api.projectId}/dr`;

class DrSource {
  async list<T>(kind: DrRecordKind, opts?: { skipLog?: boolean }): Promise<T[]> {
    const [segment] = PATHS[kind];
    const response = await Api.send({ url: `${base()}/${segment}`, quietError: true, skipLog: opts?.skipLog });
    return response.data[segment] || [];
  }

  sites = (opts?: { skipLog?: boolean }) => this.list<DrSite>("site", opts);

  policies = (opts?: { skipLog?: boolean }) => this.list<DrPolicy>("policy", opts);

  groups = (opts?: { skipLog?: boolean }) => this.list<DrGroup>("protection_group", opts);

  workloads = (opts?: { skipLog?: boolean }) => this.list<DrWorkload>("workload", opts);

  async create<T>(kind: DrRecordKind, values: { [key: string]: any }): Promise<T> {
    const [segment, key] = PATHS[kind];
    const response = await Api.send({ url: `${base()}/${segment}`, method: "POST", data: { [key]: values }, quietError: true });
    return response.data[key];
  }

  async update<T>(kind: DrRecordKind, id: string, values: { [key: string]: any }): Promise<T> {
    const [segment, key] = PATHS[kind];
    const response = await Api.send({
      url: `${base()}/${segment}/${encodeURIComponent(id)}`,
      method: "PUT",
      data: { [key]: values },
      quietError: true,
    });
    return response.data[key];
  }

  async remove(kind: DrRecordKind, id: string): Promise<void> {
    const [segment] = PATHS[kind];
    await Api.send({ url: `${base()}/${segment}/${encodeURIComponent(id)}`, method: "DELETE", quietError: true });
  }

  async workloadAction(id: string, action: DrWorkloadAction, options: { [key: string]: any } = {}): Promise<DrWorkload> {
    const response = await Api.send({
      url: `${base()}/workloads/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { [action]: options },
      quietError: true,
    });
    return response.data.workload;
  }

  async events(workloadId: string, limit = 100): Promise<DrEvent[]> {
    const response = await Api.send({
      url: `${base()}/workloads/${encodeURIComponent(workloadId)}/events?limit=${limit}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.events || [];
  }

  // ---- DR plans (Phase 9) ----------------------------------------------------------------------

  async plans(opts?: { skipLog?: boolean }): Promise<DrPlan[]> {
    const response = await Api.send({ url: `${base()}/plans`, skipLog: opts?.skipLog, quietError: true });
    return response.data.plans || [];
  }

  async plan(id: string): Promise<DrPlan> {
    const response = await Api.send({ url: `${base()}/plans/${encodeURIComponent(id)}`, skipLog: true });
    return response.data.plan;
  }

  async createPlan(values: { name: string; description?: string | null; group_ids: string[]; spec: DrPlanSpec }): Promise<DrPlan> {
    const response = await Api.send({ url: `${base()}/plans`, method: "POST", data: { plan: values }, quietError: true });
    return response.data.plan;
  }

  /** Name/description change in place; group_ids, spec or restore_version make a new version. */
  async updatePlan(id: string, values: { [key: string]: any }): Promise<DrPlan> {
    const response = await Api.send({
      url: `${base()}/plans/${encodeURIComponent(id)}`,
      method: "PUT",
      data: { plan: values },
      quietError: true,
    });
    return response.data.plan;
  }

  async archivePlan(id: string): Promise<void> {
    await Api.send({ url: `${base()}/plans/${encodeURIComponent(id)}`, method: "DELETE", quietError: true });
  }

  async planVersions(id: string): Promise<DrPlanVersion[]> {
    const response = await Api.send({ url: `${base()}/plans/${encodeURIComponent(id)}/versions`, skipLog: true });
    return response.data.versions || [];
  }

  async validatePlan(id: string): Promise<DrRun> {
    const response = await Api.send({
      url: `${base()}/plans/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { validate: {} },
      quietError: true,
    });
    return response.data.run;
  }

  async run(id: string): Promise<DrRun> {
    const response = await Api.send({ url: `${base()}/runs/${encodeURIComponent(id)}`, skipLog: true });
    return response.data.run;
  }

  /** Requests a failover of the plan. planned: a run that waits for someone else's approval in Change control;
   * emergency: starts at once (dr_emergency role), the reason is also the acknowledgment, reviewed afterwards.
   * recoveryPoints: workload ID -> recovery point ID, for workloads recovered from an older point than their newest
   * (point-in-time recovery; DR sites that keep several restore points). */
  async failover(
    planId: string,
    reason?: string,
    mode: "planned" | "emergency" | "test" | "failback" = "planned",
    recoveryPoints?: { [workloadId: string]: string },
  ): Promise<DrRun> {
    const points = recoveryPoints && Object.keys(recoveryPoints).length ? recoveryPoints : null;
    const response = await Api.send({
      url: `${base()}/plans/${encodeURIComponent(planId)}/actions`,
      method: "POST",
      data: {
        failover: { mode, ...(reason ? { reason } : {}), ...(points ? { recovery_points: points } : {}) },
      },
      quietError: true,
    });
    return response.data.run;
  }

  /** DR approvals, newest first; pendingOnly: the ones still waiting for a decision. */
  async approvals(pendingOnly = false): Promise<DrApproval[]> {
    const response = await Api.send({
      url: `${base()}/approvals${pendingOnly ? "?pending=true" : ""}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.approvals || [];
  }

  /** approve (the run starts) or reject (it is cancelled); Swift refuses it from the requester. */
  async decideApproval(id: string, action: "approve" | "reject" | "review", comment?: string): Promise<DrApproval> {
    const response = await Api.send({
      url: `${base()}/approvals/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { [action]: comment ? { comment } : {} },
      quietError: true,
    });
    return response.data.approval;
  }

  /** Measured RPO and RTO of each workload against its policy; groupId limits them to one group's. */
  async alerts(includeCleared = false): Promise<DrAlert[]> {
    const response = await Api.send({ url: `${base()}/alerts${includeCleared ? "?all=true" : ""}`, quietError: true, skipLog: true });
    return response.data.alerts;
  }

  async acknowledgeAlert(id: string, comment?: string): Promise<DrAlert> {
    const response = await Api.send({
      url: `${base()}/alerts/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { acknowledge: { comment } },
      quietError: true,
    });
    return response.data.alert;
  }

  async overview(): Promise<DrOverview> {
    const response = await Api.send({ url: `${base()}/overview`, quietError: true, skipLog: true });
    return response.data.overview;
  }

  async clientAccesses(): Promise<DrClientAccess[]> {
    const response = await Api.send({ url: `${base()}/client_access`, quietError: true, skipLog: true });
    return response.data.client_access;
  }

  async openClientAccess(projectId: string, reason: string, hours: number): Promise<DrClientAccess> {
    const response = await Api.send({
      url: `${base()}/client_access`,
      method: "POST",
      data: { client_access: { project_id: projectId, reason, hours } },
      quietError: true,
    });
    return response.data.client_access;
  }

  async closeClientAccess(id: string): Promise<void> {
    await Api.send({ url: `${base()}/client_access/${encodeURIComponent(id)}`, method: "DELETE", quietError: true });
  }

  async clientSettings(): Promise<DrClientSettings> {
    const response = await Api.send({ url: `${base()}/client_settings`, quietError: true, skipLog: true });
    return response.data.client_settings;
  }

  async updateClientSettings(settings: { [section: string]: any }, reason?: string): Promise<DrClientSettings> {
    const response = await Api.send({
      url: `${base()}/client_settings`,
      method: "PUT",
      data: { client_settings: { settings, reason } },
      quietError: true,
    });
    return response.data.client_settings;
  }

  async sla(groupId?: string): Promise<DrSla> {
    const response = await Api.send({
      url: `${base()}/sla${groupId ? `?group_id=${encodeURIComponent(groupId)}` : ""}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.sla;
  }

  /** Runs, newest first; planId limits them to one plan's. */
  async runs(planId?: string): Promise<DrRun[]> {
    const response = await Api.send({
      url: `${base()}/runs${planId ? `?plan_id=${encodeURIComponent(planId)}` : ""}`,
      skipLog: true,
    });
    return response.data.runs || [];
  }

  /** pause, resume, cancel, retry (the failed steps start again) or rollback; the reason goes to the audit log. */
  async runAction(id: string, action: DrRunAction, reason?: string): Promise<DrRun> {
    const response = await Api.send({
      url: `${base()}/runs/${encodeURIComponent(id)}/actions`,
      method: "POST",
      data: { [action]: reason ? { reason } : {} },
      quietError: true,
    });
    return response.data.run;
  }

  /** The workload's recovery points, newest first; includeExpired adds the ones the DR site no longer has. */
  async recoveryPoints(workloadId: string, includeExpired = false): Promise<DrRecoveryPoint[]> {
    const response = await Api.send({
      url: `${base()}/workloads/${encodeURIComponent(workloadId)}/recovery_points${includeExpired ? "?include_expired=true" : ""}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.recovery_points || [];
  }

  /** A negotiated pair when both endpoints are given; every provider's declaration without them. */
  async capabilities(sourceEndpointId?: string, targetEndpointId?: string): Promise<DrPairCapabilities | any> {
    const params: string[] = [];
    if (sourceEndpointId) params.push(`source_endpoint_id=${encodeURIComponent(sourceEndpointId)}`);
    if (targetEndpointId) params.push(`target_endpoint_id=${encodeURIComponent(targetEndpointId)}`);
    const response = await Api.send({
      url: `${base()}/capabilities${params.length ? `?${params.join("&")}` : ""}`,
      quietError: true,
      skipLog: true,
    });
    return response.data.capabilities;
  }
}

export default new DrSource();
